import { useState, useEffect, useCallback } from "react";
import { sqlReadRaw as sqlRead, payloadOf } from "../../utils/sqlClient";
import { FINANCIAL_COMPLIANCE_FIELDS } from "../../config/financialCompliance";

// Financial Compliance — bid-time tax/bonding/payroll assumptions, captured
// per estimate revision. Feeds pricing/margin conversations at bid time, then
// is ported once into the turnover-side Financial Compliance panel (Section 4,
// Administrative Documents) at turnover creation, where Accounting tracks the
// actual paperwork (cert numbers, expirations, receipt status) independently.

const DEFAULTS = { CertifiedPayrollRequired: false, PrevailingWage: false };

export default function FinancialCompliancePanel({ projectId }) {
  const [revisions, setRevisions]   = useState([]);
  const [activeRevId, setActiveRevId] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [formLoading, setFormLoading] = useState(false);
  const [form, setForm]             = useState(DEFAULTS);
  const [saving, setSaving]         = useState(false);

  useEffect(() => {
    setLoading(true);
    sqlRead(`EXEC sp_GetEstimateRevisions @project_id=${projectId}`)
      .then(result => {
        const revs = result?.Table1 || result?.value || [];
        setRevisions(revs);
        const active = revs.find(r => !r.IsLocked) || revs[0];
        if (active) setActiveRevId(active.RevisionID);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [projectId]);

  const loadCompliance = useCallback(() => {
    if (!activeRevId) return;
    setFormLoading(true);
    sqlRead(`EXEC sp_GetFinancialCompliance @revision_id=${activeRevId}`)
      .then(result => {
        const row = (result?.Table1 || [])[0];
        setForm(row ? { ...DEFAULTS, ...row } : DEFAULTS);
      })
      .catch(e => { console.error(e); alert("Error loading financial compliance: " + e.message); })
      .finally(() => setFormLoading(false));
  }, [activeRevId]);

  useEffect(() => { loadCompliance(); }, [loadCompliance]);

  const revision = revisions.find(r => r.RevisionID === activeRevId);
  const isLocked = !!revision?.IsLocked;

  async function save(updated) {
    if (isLocked) return;
    setForm(updated);
    setSaving(true);
    try {
      const payload = { revision_id: activeRevId };
      for (const f of FINANCIAL_COMPLIANCE_FIELDS) payload[f.param] = updated[f.key] ?? null;
      await sqlRead(`EXEC sp_SaveFinancialCompliance @payload = '${payloadOf(payload)}'`);
    } catch (e) {
      alert("Error saving financial compliance: " + e.message);
    } finally {
      setSaving(false);
    }
  }

  const labelStyle = { fontSize: 12, fontWeight: 600, color: "var(--text2)", marginBottom: 4 };
  const ctlStyle = {
    width: "100%", padding: "7px 10px", fontSize: 13,
    border: "1px solid var(--cs-border)", borderRadius: 6,
    background: "var(--surface)", color: "var(--text)", boxSizing: "border-box",
  };

  function renderField(f) {
    const value = form[f.key];
    const onChange = (v) => save({ ...form, [f.key]: v });

    if (f.type === "boolean") {
      return (
        <div key={f.key} style={{ marginBottom: 16 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: isLocked ? "default" : "pointer" }}>
            <input
              type="checkbox"
              checked={!!value}
              disabled={isLocked}
              onChange={e => onChange(e.target.checked)}
            />
            <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text2)" }}>{f.label}</span>
          </label>
          {f.hint && <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 4 }}>{f.hint}</div>}
        </div>
      );
    }

    if (f.type === "select") {
      return (
        <div key={f.key} style={{ marginBottom: 16 }}>
          <div style={labelStyle}>{f.label}</div>
          <select
            value={value || ""}
            disabled={isLocked}
            onChange={e => onChange(e.target.value || null)}
            style={{ ...ctlStyle, color: value ? "var(--text)" : "var(--text3)" }}
          >
            <option value="">— Select —</option>
            {f.options.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      );
    }

    if (f.type === "textarea") {
      return (
        <div key={f.key} style={{ marginBottom: 16 }}>
          <div style={labelStyle}>{f.label}</div>
          <textarea
            defaultValue={value || ""}
            disabled={isLocked}
            rows={3}
            onBlur={e => { if ((e.target.value || null) !== (value || null)) onChange(e.target.value || null); }}
            style={{ ...ctlStyle, resize: "vertical", fontFamily: "inherit", lineHeight: 1.5 }}
          />
        </div>
      );
    }

    return (
      <div key={f.key} style={{ marginBottom: 16 }}>
        <div style={labelStyle}>{f.label}</div>
        <input
          type={f.type === "number" ? "number" : "text"}
          defaultValue={value ?? ""}
          disabled={isLocked}
          placeholder={f.placeholder}
          onBlur={e => {
            const v = e.target.value === "" ? null : (f.type === "number" ? +e.target.value : e.target.value);
            if (v !== (value ?? null)) onChange(v);
          }}
          style={ctlStyle}
        />
      </div>
    );
  }

  if (loading) return <div style={{ color: "var(--text3)", fontSize: 13 }}>Loading…</div>;

  if (revisions.length === 0) return (
    <div style={{ maxWidth: 560 }}>
      <div className="panel-title">Financial Compliance</div>
      <div className="panel-sub">No revisions yet. Create a revision from the Revisions tab first.</div>
    </div>
  );

  return (
    <div style={{ maxWidth: 640 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div className="panel-title">Financial Compliance</div>
          <div className="panel-sub">
            Bid-time tax and compliance assumptions — feeds pricing/margin conversations now, and
            ports into the turnover&rsquo;s Administrative Documents section at turnover creation,
            where Accounting tracks the actual paperwork.
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 12, color: "var(--text3)" }}>Revision:</span>
          <select
            value={activeRevId || ""}
            onChange={e => setActiveRevId(+e.target.value)}
            style={{ height: 30, border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: "0 8px", fontSize: 12, color: "var(--text)", background: "var(--surface)", outline: "none" }}
          >
            {revisions.map(r => (
              <option key={r.RevisionID} value={r.RevisionID}>
                Rev {r.RevisionNumber}: {r.RevisionLabel}{r.IsLocked ? " 🔒" : ""}
              </option>
            ))}
          </select>
          {isLocked && (
            <span style={{ fontSize: 11, color: "#d97706", fontWeight: 600, padding: "2px 8px", borderRadius: 100, background: "var(--tint-amber)" }}>
              Locked — create new revision to edit
            </span>
          )}
        </div>
      </div>

      {formLoading ? (
        <div style={{ color: "var(--text3)", fontSize: 13 }}>Loading…</div>
      ) : (
        <div style={{ padding: "16px 18px", background: "var(--surface2)", border: "1px solid var(--cs-border)", borderRadius: 10 }}>
          {FINANCIAL_COMPLIANCE_FIELDS.map(renderField)}
          {saving && <div style={{ fontSize: 11, color: "var(--text3)" }}>Saving…</div>}
        </div>
      )}
    </div>
  );
}
